import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { handleSearchQuery } from '../../manager/components/context';
import { useCurrentPageFilters } from '../../../utils/filters';
import useModerationContext from '../../../context/useModerationContext';
import { ModerationAttendee as AttendeeModel, Event, ModerationFlowsModel, ModerationStatuses, ModerationRates } from '../../../models';

import { Button, Icon, WeezHeader, Card, ContentCard, Table, Search, AdvancedFilter, CheckboxLabel, Dropdown, DropdownItem, ToastContext, StopPropagation } from '@weezevent/nacre';
import LoadingTable from '../../../components/loadingTable';
import SendMailWithStatusModal from '../components/sendMailWithStatusModal';
import { InfoTextTooltip } from '../../attendee/detail/components/infotext-tooltip/infoTextTooltip';
import { DateInTable } from '../components/DateInTable';
import { quickFilter } from '../../../utils/tools';
import { Loader } from 'semantic-ui-react';
import ModerationPaginator from '../components/moderationPaginator';

import Styles from './moderationList.module.css';

const spec = {
    multiple: ['status_id', 'event_id', 'moderation_flow_id'],
    fields: ['status_id', 'event_id', 'moderation_flow_id', 'search'],
    aliases: {},
    date: [],
    form: []
};

/**
 * Entry point for attendees moderation.
 */
export default function ModerationAttendees({ organization, flowId }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const [{ flow }] = useModerationContext();
    const [currentPage, setCurrentPage] = React.useState(0);
    const [pageSize] = React.useState(50);
    const [filters, setFilters] = useCurrentPageFilters(spec);
    const defaultFilters = {};

    const [openModal, setOpenModal] = React.useState(false);
    const [nextAttendeeLoading, setNextAttendeeLoading] = React.useState(false);
    const [pendingFilter, setPendingFilter] = React.useState(null);
    const [eventsFilterOptions, setEventsFilterOptions] = React.useState([]);
    const [ratesFilterOptions, setRatesFilterOptions] = React.useState([]);

    const [flowAttendees, loadingFlowAttendees] = AttendeeModel.useApiModel(
        {
            organizationId: organization.id,
            flowId,
            cache: false,
            allow_cache: false,
            query: {
                limit: pageSize,
                offset: currentPage * pageSize,
                ...handleSearchQuery(spec, filters)
            }
        },
        [organization, filters, currentPage, flowId, pageSize, handleSearchQuery]
    );

    const [flows, loadingFlows] = ModerationFlowsModel.useApiModel(
        {
            organizationId: organization.id,
            cache: false,
            allow_cache: false
        },
        [organization]
    );

    ModerationRates.useApiModel(
        {
            organizationId: organization.id,
            flowId,
            onSuccess: response => {
                setRatesFilterOptions(response.map(ev => ({ ...ev.toOptions() })));
            }
        },
        [organization, flowId]
    );

    Event.useApiModel(
        {
            organizationId: organization.id,
            query: {
                moderation_flow_exists: true
            },
            onSuccess: response => {
                setEventsFilterOptions(response.map(ev => ({ ...ev.toOptions(), name: String(ev.id) })));
            }
        },
        [organization]
    );

    const [statuses, loadingStatuses] = ModerationStatuses.useApiModel(
        {
            organizationId: organization.id,
            flowId,
            cache: false,
            allow_cache: false,
            onSuccess: response => {
                setPendingFilter(
                    response
                        .filter(st => st.stage === 'pending')
                        .map(st => st.id)
                        .join()
                );
            }
        },
        [organization, flowId]
    );

    const moderationFlowsFilterOptions = React.useMemo(() => {
        if (!flows || loadingFlows) {
            return [];
        }
        return flows?.map(flow => flow.toOptions());
    }, [flows, loadingFlows]);

    const handleSearch = React.useCallback(
        value => {
            let _filters = { ...filters, search: value };
            if (!value) delete _filters['search'];
            setFilters(_filters);
        },
        [filters]
    );

    const handleQuickFilters = React.useCallback(
        (name, value, checked) => {
            setFilters(quickFilter(filters, name, value, checked));
        },
        [filters]
    );

    const handleRemoved = React.useCallback(
        checked => {
            let _filters = { ...filters, include_removed: true };

            if (!checked) {
                delete _filters['include_removed'];
            }
            setFilters(_filters);
        },
        [filters]
    );

    const handleNextAttendee = React.useCallback(() => {
        setNextAttendeeLoading(true);
        AttendeeModel.getNextAttendee({
            organizationId: organization.id,
            flowId,
            query: window.location.search
        })
            .then(response => {
                history.push(response.url);
            })
            .catch(e => {
                toast.error(t('ticket.moderation.attendee.nexAttendeeError'));
            })
            .finally(e => {
                setNextAttendeeLoading(false);
            });
    }, [organization, flowId]);

    const totalCount = React.useMemo(() => {
        if (!flowAttendees || loadingFlowAttendees) {
            return;
        }
        return +flowAttendees?.__meta?.headers?.get('total-count');
    }, [flowAttendees, loadingFlowAttendees]);

    const statusesOptions = React.useMemo(() => {
        if (!statuses || loadingStatuses) {
            return [];
        }
        return statuses.map(status => status.toOptions(flowId));
    }, [statuses, loadingStatuses, flowId]);

    return (
        <>
            <WeezHeader
                title={t('ticket.moderation.attendees.title')}
                rightComponent={[
                    <Button
                        key={0}
                        primary
                        icon={<Icon name="check-shield" />}
                        label={nextAttendeeLoading ? <Loader className={Styles['loader-count-loader']} active /> : t('ticket.moderation.attendees.headerCTA')}
                        onClick={() => handleNextAttendee()}
                    />
                ]}
            >
                {t('ticket.moderation.attendees.subtitle')}
            </WeezHeader>
            <Card>
                <ContentCard>
                    <AdvancedFilter
                        value={filters}
                        onChange={values => setFilters(values ?? defaultFilters)}
                        total={{
                            count: totalCount,
                            loading: loadingFlowAttendees,
                            label: t('ticket.moderation.attendees.table.countTotal', { count: totalCount || 0 })
                        }}
                        filters={[
                            {
                                name: 'status_id',
                                labelName: t('ticket.moderation.attendees.table.filters.status'),
                                options: statusesOptions,
                                multiple: true,
                                index: 1
                            },
                            {
                                name: 'moderation_flow_id',
                                labelName: t('ticket.moderation.attendees.table.filters.flow'),
                                options: moderationFlowsFilterOptions,
                                multiple: true,
                                index: 2
                            },
                            {
                                name: 'event_id',
                                labelName: t('ticket.moderation.attendees.table.filters.event'),
                                options: eventsFilterOptions,
                                multiple: true,
                                index: 3
                            },
                            {
                                name: 'rate_id',
                                labelName: t('ticket.moderation.attendees.table.filters.rate'),
                                options: ratesFilterOptions,
                                multiple: true,
                                index: 4
                            }
                        ]}
                        translations={{
                            getApplyFilters: () => t('ticket.common.cta.apply'),
                            labelFilters: t('ticket.common.filters'),
                            labelReset: t('ticket.common.action.reset'),
                            labelFilterBy: t('ticket.common.action.filter_by'),
                            labelCancel: t('ticket.common.cta.cancel')
                        }}
                        leftComponent={
                            <Search
                                icon="search"
                                value={filters?.search}
                                onChange={(_, value) => handleSearch(value)}
                                handleReset={() => setFilters(filters => ({ ...filters, search: '' }))}
                            />
                        }
                    />
                    <div className={Styles['moderation-list-subfilters-container']}>
                        <div className={Styles['moderation-list-subfilters-quickfilter-container']}>
                            <span>{t('ticket.moderation.attendees.table.quickFilter')}</span>
                            <CheckboxLabel
                                className={Styles['moderation-list-subfilters-quickfilter-checkbox']}
                                label={t('ticket.moderation.attendees.table.pendingRequests')}
                                checked={filters.status_in === pendingFilter || filters.status_in?.has(pendingFilter)}
                                onClick={({ target: { checked } }) => handleQuickFilters('status_id', pendingFilter, checked)}
                            />
                            {flow && flow.allow_removed_attendees && (
                                <CheckboxLabel
                                    className={Styles['moderation-list-subfilters-quickfilter-checkbox']}
                                    label={t('ticket.moderation.flows.create.attendees_toggle_label')}
                                    checked={filters.include_removed}
                                    onClick={({ target: { checked } }) => handleRemoved(checked)}
                                />
                            )}
                        </div>
                    </div>
                </ContentCard>
                <Table useNew>
                    <Table.Header>
                        <Table.Tr>
                            <th>{t('ticket.moderation.attendees.table.date')}</th>
                            <th>{t('ticket.moderation.attendees.table.attendeeInfos')}</th>
                            <th>{t('ticket.moderation.attendees.table.barcode')}</th>
                            <th>{t('ticket.moderation.attendees.table.order')}</th>
                            <th>{t('ticket.moderation.attendees.table.rateAndEvent')}</th>
                            <th>{t('ticket.moderation.attendees.table.status')}</th>
                            <th>{t('ticket.moderation.attendees.table.actions')}</th>
                        </Table.Tr>
                    </Table.Header>
                    {flowAttendees && !loadingFlowAttendees && (
                        <Table.TBody>
                            {flowAttendees?.map(attendee => (
                                <FlowAttendeeRow
                                    key={attendee.id}
                                    attendee={attendee}
                                    setOpenModal={setOpenModal}
                                    organizationId={organization.id}
                                    flowId={flowId}
                                    statusesOptions={statusesOptions}
                                />
                            ))}
                        </Table.TBody>
                    )}
                </Table>
                <LoadingTable loading={loadingFlowAttendees} height="30vh" />
                {!loadingFlowAttendees && !flowAttendees?.length && (
                    <Card.EmptyState title={t('ticket.moderation.attendees.emptyTitle')} subtitle={t('ticket.moderation.attendees.emptySubtitle')} icon={<Icon name="list" />} />
                )}
                <ModerationPaginator currentPage={currentPage} setCurrentPage={setCurrentPage} totalCount={totalCount || 0} pageSize={pageSize} />
                <SendMailWithStatusModal open={openModal} setOpenModal={setOpenModal} />
            </Card>
        </>
    );
}

const FlowAttendeeRow = ({ attendee, setOpenModal, organizationId, flowId, statusesOptions }) => {
    const { t } = useTranslation();
    const history = useHistory();

    const redirectUri = React.useMemo(() => {
        if (!flowId) {
            return `/ticket/O${organizationId}/moderation/moderation_attendees/${attendee.id}`;
        }
        return `/ticket/O${organizationId}/moderation/moderation_flows/${flowId}/moderation_attendees/${attendee.id}`;
    }, [organizationId, flowId, attendee]);

    const handleDownloadTicket = React.useCallback(() => {
        window.open(attendee?.ticket_url, '_blank');
    }, [attendee]);

    return (
        <Table.Tr
            className={`${Styles['moderation-list-table-status']} ${attendee.status.stage} ${attendee.removed ? Styles['tr-removed'] : ''}`}
            onClick={() => history.push(redirectUri)}
        >
            <Table.Td>{<DateInTable date={attendee.created} timezone={attendee.event_timezone} language={attendee.event_default_language} />}</Table.Td>
            <Table.Td>
                <InfoTextTooltip title={`${attendee.attendee_last_name.toUpperCase()} ${attendee.attendee_first_name}`} subtitle={attendee.attendee_email} tooltip={false} />
            </Table.Td>
            <Table.Td>
                <InfoTextTooltip tooltipCopy title={attendee.barcode} subtitle={attendee.id_weez_ticket} tooltip={false} />
            </Table.Td>
            <Table.Td>
                <InfoTextTooltip tooltipCopy title={attendee.order_reference} subtitle={attendee.order_id} />
            </Table.Td>
            <Table.Td>
                <InfoTextTooltip title={attendee.rate_name} subtitle={attendee.event_name.toUpperCase()} tooltip={false} />
            </Table.Td>
            <Table.Td>
                <InfoTextTooltip title={attendee.status.name} subtitle={attendee.moderation_flow_name} tooltip={false} />
            </Table.Td>
            <Table.Td>
                <Dropdown closeOnDocumentClick size="medium">
                    <StopPropagation>
                        <DropdownItem item={t('ticket.moderation.attendees.table.actionSeeAttendee')} />
                        {/*@TODO Uncomment when the feature is ready*/}
                        {/*<DropdownItem item={t('ticket.moderation.attendees.table.actionSendMail')} onClick={() => setOpenModal(true)} />*/}
                        <DropdownItem item={t('ticket.moderation.attendees.table.actionDownloadTicket')} disabled={!attendee.ticket_url} onClick={handleDownloadTicket} />
                    </StopPropagation>
                </Dropdown>
            </Table.Td>
        </Table.Tr>
    );
};
